import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { Upload, Download, Trash2, FileText, Camera, Award } from "lucide-react";
import { motion } from "framer-motion";
import { API } from "@/App";
import { handleBlobDownload } from "./utils";

export function CertificatesTab({ classId, certificates, students, headers, onRefresh }) {
  const [certDialog, setCertDialog] = useState(false);
  const [certFile, setCertFile] = useState(null);
  const [certTitle, setCertTitle] = useState("");
  const [certStudentId, setCertStudentId] = useState("");
  const [uploadingCert, setUploadingCert] = useState(false);
  const certFileRef = useRef(null);
  const certCameraRef = useRef(null);

  const handleUploadCertificate = async () => {
    if (!certFile || !certStudentId || !certTitle) {
      toast.error("Please select a student, enter a title, and choose a file");
      return;
    }
    setUploadingCert(true);
    const formData = new FormData();
    formData.append("file", certFile);
    formData.append("class_id", classId);
    formData.append("student_id", certStudentId);
    formData.append("title", certTitle);
    try {
      await axios.post(`${API}/certificates`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      toast.success("Certificate uploaded!");
      setCertDialog(false);
      setCertFile(null);
      setCertTitle("");
      setCertStudentId("");
      if (certFileRef.current) certFileRef.current.value = "";
      if (certCameraRef.current) certCameraRef.current.value = "";
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload certificate");
    } finally {
      setUploadingCert(false);
    }
  };

  const handleDeleteCertificate = async (certificateId) => {
    if (!window.confirm("Delete this certificate?")) return;
    try {
      await axios.delete(`${API}/certificates/${certificateId}`, { headers });
      toast.success("Certificate deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete certificate");
    }
  };

  const handleDownloadCertificate = (certificateId, filename) => {
    handleBlobDownload(`${API}/certificates/${certificateId}/download`, filename, headers);
  };

  return (
    <Card className="border-border/50">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-['Manrope']">Certificates</CardTitle>
          <CardDescription>Upload certificates for students</CardDescription>
        </div>
        <Dialog open={certDialog} onOpenChange={(open) => {
          setCertDialog(open);
          if (!open) { setCertFile(null); setCertTitle(""); setCertStudentId(""); }
        }}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-primary text-primary-foreground" data-testid="upload-cert-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload Certificate
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Upload Certificate</DialogTitle>
              <DialogDescription>Upload a certificate for a student</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Student</Label>
                <select
                  value={certStudentId}
                  onChange={(e) => setCertStudentId(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  data-testid="cert-student-select"
                >
                  <option value="">Select a student...</option>
                  {students.map(s => (
                    <option key={s.user_id} value={s.user_id}>{s.name} ({s.email})</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Certificate Title</Label>
                <Input placeholder="e.g., Course Completion Certificate" value={certTitle} onChange={(e) => setCertTitle(e.target.value)} data-testid="cert-title-input" />
              </div>
              <div className="border-2 border-dashed border-border rounded-xl p-6 text-center">
                <input type="file" ref={certFileRef} accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setCertFile(e.target.files?.[0] || null)} className="hidden" data-testid="cert-file-input" />
                <input type="file" accept="image/*" capture="environment" ref={certCameraRef} onChange={(e) => setCertFile(e.target.files?.[0] || null)} className="hidden" data-testid="cert-camera-input" />
                {certFile ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground truncate max-w-[250px]">{certFile.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => { setCertFile(null); if (certFileRef.current) certFileRef.current.value = ""; if (certCameraRef.current) certCameraRef.current.value = ""; }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Award className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">Upload PDF or image</p>
                    <div className="flex items-center justify-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => certFileRef.current?.click()} data-testid="cert-choose-file-btn">
                        <FileText className="w-4 h-4 mr-1" />
                        Choose File
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => certCameraRef.current?.click()} data-testid="cert-camera-btn">
                        <Camera className="w-4 h-4 mr-1" />
                        Take Photo
                      </Button>
                    </div>
                  </>
                )}
              </div>
              <Button onClick={handleUploadCertificate} className="w-full rounded-full bg-primary text-primary-foreground" disabled={!certFile || !certStudentId || !certTitle || uploadingCert} data-testid="cert-upload-submit-btn">
                {uploadingCert ? "Uploading..." : "Upload Certificate"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {certificates.length === 0 ? (
          <div className="text-center py-12">
            <Award className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No certificates uploaded yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {certificates.map((cert) => (
              <motion.div
                key={cert.certificate_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-between p-4 rounded-xl border border-border/50 hover:border-primary/30 transition-all"
                data-testid={`cert-${cert.certificate_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{cert.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {cert.student_name} · {format(new Date(cert.created_at), "MMM d, yyyy")}
                    </p>
                    <p className="text-xs text-muted-foreground">{cert.original_filename}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => handleDownloadCertificate(cert.certificate_id, cert.original_filename)} data-testid={`download-cert-${cert.certificate_id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => handleDeleteCertificate(cert.certificate_id)} data-testid={`delete-cert-${cert.certificate_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
